import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { useUpgradeState } from '../../modules/entitlements';
import { usePermission } from '../../modules/auth/hooks';
import { PERMISSIONS } from '../../modules/auth/permissions';
import { useSettingsDialog } from '../settings/useSettingsDialog';

/** Height is pinned to `--banner-h` (2.25rem) so the fixed sidebar can offset beneath it. */
export function SubscriptionAlert() {
  const { t } = useTranslation();
  const { open: openSettings } = useSettingsDialog();
  const canEdit = usePermission(PERMISSIONS.BILLING_EDIT);
  const { reason, daysLeft, bannerSeverity } = useUpgradeState();

  if (!bannerSeverity || !reason) return null;

  const messages: Record<string, string> = {
    past_due: t(
      'your_payment_is_past_due_please_update_your_payment_method_to_avoid_losing_access',
      'Your payment is past due. Update your payment method to avoid losing access.',
    ),
    unpaid: t(
      'your_subscription_is_unpaid_please_update_your_payment_method_immediately',
      'Your subscription is unpaid. Update your payment method immediately.',
    ),
    canceled: t(
      'subscription_canceled_banner',
      'Your subscription was canceled. Upgrade to restore access.',
    ),
    expired: t('plan_expired_banner', 'Your plan has expired. Upgrade to restore access.'),
    trial_ending: t('trial_ending_banner', {
      count: daysLeft ?? 0,
      defaultValue: `Your free trial ends in ${daysLeft ?? 0} days. Upgrade to keep your features.`,
    }),
    trial_over: t('trial_over_banner', 'Your free trial has ended. Upgrade to continue.'),
  };

  const message =
    messages[reason] ??
    t('there_is_an_issue_with_your_subscription', 'There is an issue with your subscription.');

  // Payment reasons keep the "Update payment" CTA; expiry/trial reasons say "Upgrade".
  const isPaymentReason = reason === 'past_due' || reason === 'unpaid';
  const ctaLabel = isPaymentReason
    ? t('update_payment', 'Update payment')
    : t('upgrade_plan', 'Upgrade plan');

  const isDanger = bannerSeverity === 'danger';
  // Payment/expiry problems get the alert glyph; a healthy-but-ending trial gets a clock.
  const Icon = isDanger ? AlertTriangle : Clock;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'relative z-40 flex h-9 shrink-0 items-center justify-center gap-2 border-b px-4',
        isDanger
          ? 'border-destructive/20 bg-destructive/10 text-destructive'
          : 'border-warning-500/25 bg-warning-500/10 text-warning-800 dark:text-warning-300',
      )}
    >
      {/* A plain glyph in the banner's colour — hidden on the narrowest screens to protect the message */}
      <Icon className="hidden h-3.5 w-3.5 shrink-0 sm:block" aria-hidden />

      <p className="min-w-0 truncate text-[13px] font-medium">{message}</p>

      {canEdit && (
        <button
          type="button"
          onClick={() => openSettings('billing')}
          className={cn(
            'group inline-flex h-6 shrink-0 items-center gap-1 rounded-md px-2.5 text-[11px] font-semibold text-white shadow-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent',
            isDanger
              ? 'bg-destructive hover:bg-destructive/90 focus-visible:ring-destructive'
              : 'bg-warning-600 hover:bg-warning-700 focus-visible:ring-warning-600',
          )}
        >
          <span className="hidden sm:inline">{ctaLabel}</span>
          <span className="sm:hidden">{isPaymentReason ? t('pay', 'Pay') : t('upgrade', 'Upgrade')}</span>
          <ArrowRight
            className="h-3 w-3 transition-transform [.group:hover_&]:translate-x-0.5"
            aria-hidden
          />
        </button>
      )}
    </div>
  );
}
